
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { API_URL } from '../config/api';
import './HeroSlider.css';

const DEFAULT_SLIDES = [
    {
        id: 1,
        image: '/images/slider/hero-slide-1.webp',
        title: 'Genética de precisión para diagnósticos confiables',
        subtitle: 'Un mejor futuro para la salud del paciente a través de la ciencia y el cuidado humano.',
        primaryAction: { label: 'Agendar Cita', link: '/contacto' },
        secondaryAction: { label: 'Ver Servicios', link: '/servicios' }
    }
];

const HeroSlider = () => {
    const [slides, setSlides] = useState(DEFAULT_SLIDES);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSlides = async () => {
            try {
                const response = await fetch(`${API_URL}/content`);
                const data = await response.json();

                if (data.success && data.data) {
                    const content = data.data;
                    const count = parseInt(content['home.hero_carousel_count']) || 3;

                    if (count > 0) {
                        const newSlides = [];
                        for (let i = 1; i <= count; i++) {
                            const imagePath = content[`home.hero_slide_${i}_image`];
                            // Handle both full URLs and relative paths
                            let image = null;
                            if (imagePath) {
                                if (imagePath.startsWith('http')) {
                                    image = imagePath;
                                } else {
                                    // Ensure clean concatenation
                                    const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
                                    const path = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
                                    image = `${baseUrl}${path}`;
                                }
                            }

                            if (image) {
                                newSlides.push({
                                    id: i,
                                    image: image,
                                    title: content[`home.hero_slide_${i}_title`] || '',
                                    subtitle: content[`home.hero_slide_${i}_subtitle`] || '',
                                    primaryAction: {
                                        label: content[`home.hero_slide_${i}_btn_text`] || '',
                                        link: content[`home.hero_slide_${i}_btn_link`] || '#'
                                    },
                                    secondaryAction: null // Simplified for dynamic content for now
                                });
                            }
                        }

                        if (newSlides.length > 0) {
                            setSlides(newSlides);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching slider content:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSlides();
    }, []);

    const nextSlide = React.useCallback(() => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setTimeout(() => setIsAnimating(false), 500);
    }, [isAnimating, slides.length]);

    const prevSlide = React.useCallback(() => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
        setTimeout(() => setIsAnimating(false), 500);
    }, [isAnimating, slides.length]);

    // Auto-advance
    useEffect(() => {
        if (slides.length <= 1) return;

        const interval = setInterval(() => {
            nextSlide();
        }, 3500); // 3.5 seconds per slide (starts after animation ends)

        return () => clearInterval(interval);
    }, [nextSlide, slides.length]);

    const goToSlide = (index) => {
        if (isAnimating || currentSlide === index) return;
        setIsAnimating(true);
        setCurrentSlide(index);
        setTimeout(() => setIsAnimating(false), 500);
    };

    if (loading) {
        return <div className="hero-slider-loading"></div>;
    }

    return (
        <section className="hero-slider">
            {slides.map((slide, index) => (
                <div
                    key={slide.id}
                    className={`slider-item ${index === currentSlide ? 'active' : ''}`}
                    style={{ backgroundImage: `url(${slide.image})` }}
                >
                    <div className="slider-overlay"></div>
                    <div className="slider-content">
                        <div className="container">
                            <h1>{slide.title}</h1>
                            <p>{slide.subtitle}</p>
                            <div className="slider-actions">
                                {slide.primaryAction?.label && (
                                    <Link to={slide.primaryAction.link} className="btn btn-primary">
                                        {slide.primaryAction.label}
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ))}

            {/* Navigation Controls */}
            {slides.length > 1 && (
                <>
                    <button className="slider-control prev" onClick={prevSlide} aria-label="Anterior">
                        <ChevronLeft size={32} />
                    </button>
                    <button className="slider-control next" onClick={nextSlide} aria-label="Siguiente">
                        <ChevronRight size={32} />
                    </button>

                    <div className="slider-indicators">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                className={`indicator-dot ${index === currentSlide ? 'active' : ''}`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Ir a diapositiva ${index + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
};

export default HeroSlider;
